import React from 'react';
import Box from '@mui/material/Box';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Carte du Design System V2 (`.card` + `.card-header` + `.card-body`).
 * En-tête optionnel (pastille d'icône teintée + titre Poppins uppercase + slot `action`),
 * corps à padding standard. Présentation pure, pilotée par les jetons `dsV2`.
 */
export type CardTone = 'bleu' | 'vert' | 'violet' | 'beige' | 'neutral';

const cardIconTone = (tone: CardTone): { bg: string; fg: string } => {
  switch (tone) {
    case 'bleu':
      return { bg: dsV2.color.bleuDim, fg: dsV2.color.bleu };
    case 'vert':
      return { bg: dsV2.color.vertDim, fg: dsV2.color.vert };
    case 'violet':
      return { bg: dsV2.color.violetDim, fg: dsV2.color.violet };
    case 'beige':
      return { bg: dsV2.color.beigeDim, fg: dsV2.color.t2 };
    case 'neutral':
    default:
      return { bg: dsV2.color.surface2, fg: dsV2.color.t2 };
  }
};

export type CardProps = {
  icon?: React.ReactNode;
  iconTone?: CardTone;
  title?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  bodySx?: object;
  id?: string;
};

export const Card = ({ icon, iconTone = 'neutral', title, action, children, bodySx, id }: CardProps) => {
  const tint = cardIconTone(iconTone);
  return (
    <Box
      id={id}
      component="section"
      sx={{
        backgroundColor: dsV2.color.blanc,
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.lg,
        boxShadow: dsV2.shadow.sm,
        overflow: 'hidden',
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: dsV2.shadow.hover },
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            padding: '13px 18px 11px',
            borderBottom: `1px solid ${dsV2.color.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            {icon ? (
              <Box
                sx={{
                  width: 27,
                  height: 27,
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: `1px solid ${dsV2.color.borderLight}`,
                  backgroundColor: tint.bg,
                  color: tint.fg,
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Box
              sx={{
                fontFamily: dsV2.font.sansBold,
                fontWeight: 700,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: dsV2.color.noir,
              }}
            >
              {title}
            </Box>
          </Box>
          {action}
        </Box>
      )}
      <Box sx={{ padding: '16px 18px', ...bodySx }}>{children}</Box>
    </Box>
  );
};

export default Card;
